import type { DateString, SortDirection } from "@/types/common"
import type { Checkup } from "@/types/exam"
import type { Patient, PatientOverview, PatientsList, PatientStatus } from "@/types/patient"
import { API_ROOT, HttpClient } from "@/api/httpClient"
import exams from "./exams"
import periods from "./periods"
import pregnancies from "./pregnancies"
import sieog from "./sieog"
import therapies from "./therapies"
import visits from "./visits"
import weights from "./weights"

export type PatientsListSortKey = "connection" | "name" | "surname" | "pregnancy_end" | "delivery_date"

export interface PatientsListQuery {
	perPage: number
	page: number
	sort?: {
		key?: PatientsListSortKey
		direction?: SortDirection
	}
	filters?: {
		/** cerca in: nane, surname, email, phone, fiscal code */
		text?: string
		status?: PatientStatus
		/** number of days until delivery */
		deliveringIn?: number
	}
}

interface PatientsListParams {
	paginate: true
	per_page: number
	page: number
	sort_key?: PatientsListSortKey
	sort_dir?: SortDirection
	searchTerm?: string
	status?: PatientStatus
	/** number of days until delivery */
	delivering_in?: number
}

export interface PatientPayload {
	name: string
	surname: string
	email: string
	phone: string
	notes: string
	/** YYYY-MM-DD HH:mm:ss */
	birthdate: DateString | null
	fiscal_code: string
	blood_group: string
	menarche_age: string
	hypertension: string
	diabetes: string
	hereditary_diseases: string
	surgical_interventions: string
	allergies: string
	drugs: string
	other_medical_history: string
	periods: string
	previous_pregnancies: string
	/** YYYY-MM-DD HH:mm:ss */
	last_pap_test_at: DateString | null
	last_pap_test_outcome: string
	/** YYYY-MM-DD HH:mm:ss */
	last_medical_visit_at: DateString | null
	/** YYYY-MM-DD HH:mm:ss */
	last_ultrasound_at: DateString | null
	is_menopause: boolean
	/** YYYY-MM-DD HH:mm:ss */
	last_period_before_menopause_at: DateString | null
}

export default {
	list(payload: PatientsListQuery = { perPage: 50, page: 1 }, signal?: AbortSignal) {
		const params: PatientsListParams = {
			paginate: true,
			per_page: payload.perPage,
			page: payload.page,
			sort_dir: payload?.sort?.direction || "desc",
			sort_key: payload?.sort?.key || "name"
		}

		if (payload.filters?.text !== undefined) {
			params.searchTerm = payload.filters.text
		}

		if (payload.filters?.status !== undefined) {
			params.status = payload.filters.status
		}

		if (payload.filters?.deliveringIn !== undefined) {
			params.delivering_in = payload.filters.deliveringIn
		}

		return HttpClient.get<PatientsList>(`${API_ROOT}/medical-centers/patients`, {
			params,
			signal
		})
	},
	upsert(patientId: number | null, payload: Partial<PatientPayload>) {
		return HttpClient.post<Patient>(`${API_ROOT}/medical-centers/patients/${patientId || ""}`, payload)
	},
	details(patientId: number) {
		return HttpClient.get<Patient>(`${API_ROOT}/medical-centers/patients/${patientId}`)
	},
	overview(patientId: number) {
		return HttpClient.get<PatientOverview>(`${API_ROOT}/medical-centers/patients/${patientId}/overview`)
	},
	delete(patientId: number) {
		return HttpClient.delete(`${API_ROOT}/medical-centers/patients/${patientId}`)
	},
	checkup() {
		return HttpClient.get<Checkup[]>(`${API_ROOT}/patients/checkups`)
	},
	weights,
	exams,
	pregnancies,
	periods,
	therapies,
	visits,
	sieog
}
